import React, { useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, Quote, ArrowRight, Play, X, Building2, TreePine } from 'lucide-react';
import { Link } from 'react-router-dom';
type VideoTestimonial = {
  id: string;
  name: string;
  role: string;
  property: string;
  category: 'Building' | 'Land';
  // YouTube video ID
  videoId: string;
  thumbnail?: string;
  quote: string;
};
// Real video testimonials from Zylus Homes' YouTube channel.
const videoTestimonials: VideoTestimonial[] = [
// ===== LAND =====
{
  id: 'land-1',
  name: 'Mrs. Folake Adesanya',
  role: 'Land Investor',
  property: 'Tiwa Reserve Plots',
  category: 'Land',
  videoId: 'qnRxRuxRW3M',
  quote: 'I bought my plot and the value has more than doubled. Zylus truly knows location.'
}, {
  id: 'land-2',
  name: 'Mr. Adewale Ogunbiyi',
  role: 'Land Banker',
  property: 'Cityview Park',
  category: 'Land',
  videoId: 'eR63qOMrE58',
  quote: 'Documentation was clean, the title is verifiable, and the estate is fully fenced. Outstanding service.'
}, {
  id: 'land-3',
  name: 'Hajiya Zainab Ibrahim',
  role: 'Land Investor',
  property: 'Park Lane Phase II',
  category: 'Land',
  videoId: 'xWDbdxTAzsY',
  quote: 'Flexible payment plan, fast title processing, and a layout that truly impressed me on inspection day.'
}, {
  id: 'land-4',
  name: 'Mr. Chinedu Okeke',
  role: 'First-time Buyer',
  property: 'Tiwa Garden City',
  category: 'Land',
  videoId: 'GIWxjBs58uA',
  quote: 'My first land investment and Zylus made it simple. From inspection to allocation, everything was smooth.'
}, {
  id: 'land-5',
  name: 'Mrs. Yetunde Bamgbose',
  role: 'Diaspora Investor',
  property: 'Cityview Park',
  category: 'Land',
  videoId: '3RuYVrEBdAU',
  quote: 'I bought from abroad with full confidence. The team kept me updated through every single step.'
}, {
  id: 'land-6',
  name: 'Engr. Tunde Olawale',
  role: 'Land Investor',
  property: 'Tiwa Reserve Plots',
  category: 'Land',
  videoId: 'UwiD9rkcYuc',
  quote: 'Strategic location, verified title, and a strong community. Zylus delivered exactly what they promised.'
}, {
  id: 'land-7',
  name: 'Mrs. Ngozi Eluemunor',
  role: 'Land Banker',
  property: 'Park Lane Phase II',
  category: 'Land',
  videoId: 'kZHhJb5SCSY',
  quote: 'Professional, transparent, and patient. They walked me through every detail of the purchase.'
}, {
  id: 'land-8',
  name: 'Mr. Babatunde Akinsanya',
  role: 'Land Investor',
  property: 'Tiwa Garden City',
  category: 'Land',
  videoId: '8zDgxqIdnYQ',
  quote: 'I have referred three friends already. The estate appreciates fast and the title is rock solid.'
}, {
  id: 'land-9',
  name: 'Dr. Aisha Mohammed',
  role: 'Land Banker',
  property: 'Cityview Park',
  category: 'Land',
  videoId: 'UPDTo2YaylA',
  quote: 'Excellent customer service. The Zylus team treated me like family throughout the entire process.'
}, {
  id: 'land-10',
  name: 'Mr. Emeka Nwachukwu',
  role: 'Land Investor',
  property: 'Tiwa Reserve Plots',
  category: 'Land',
  videoId: 'LYxCJ3Mx_cE',
  quote: 'Best decision I made this year. The infrastructure is moving faster than promised.'
}, {
  id: 'land-11',
  name: 'Mrs. Bukola Sanusi',
  role: 'First-time Buyer',
  property: 'Park Lane Phase II',
  category: 'Land',
  videoId: '1WqqtebAQ9M',
  quote: 'Affordable entry into real estate without compromising on security and proper documentation.'
}, {
  id: 'land-12',
  name: 'Mr. Olumide Bankole',
  role: 'Land Investor',
  property: 'Tiwa Garden City',
  category: 'Land',
  videoId: 'Hj9OBzm6P7M',
  quote: 'Zylus stands by their word. Allocation was on time and the layout matches the brochure exactly.'
},
// ===== BUILDING =====
{
  id: 'build-1',
  name: 'Mr. Adekunle Ogundimu',
  role: 'Homeowner',
  property: 'Zylus Court Phase II',
  category: 'Building',
  videoId: 'kRJBlu-BIEQ',
  quote: 'From site inspection to handover, Zylus delivered exactly what was promised. My family is settled and proud.'
}, {
  id: 'build-2',
  name: 'Engr. Chukwuemeka Okonkwo',
  role: 'Diaspora Investor',
  property: 'Lekki Pride III',
  category: 'Building',
  videoId: 'p6JFw_c2b38',
  quote: 'I bought from abroad without ever stepping on the site. Their transparency convinced me — and they delivered.'
}, {
  id: 'build-3',
  name: 'Mr. Bisi Akinwale',
  role: 'Homeowner',
  property: 'Tiwa Garden City',
  category: 'Building',
  videoId: 'cATYjVawmZ8',
  quote: 'The build quality is what sold me. You can feel the craftsmanship the moment you walk through the door.'
}];
const writtenTestimonials = [{
  id: 1,
  name: 'Oluwaseun Adebayo',
  location: 'Lekki, Lagos',
  property: 'Tiwa Garden City',
  quote: 'Investing in Tiwa Garden City was the best decision for my family. The allocation process was seamless, and the infrastructure development is moving faster than promised.',
  avatar: 'https://images.unsplash.com/photo-1531384441138-2736e62e0919?w=150&q=80'
}, {
  id: 2,
  name: 'Chukwudi Eze',
  location: 'Abuja',
  property: 'Cityview Park',
  quote: 'As a diaspora investor, trust is everything. Zylus Homes provided transparent documentation and virtual tours that made me confident in my purchase.',
  avatar: 'https://images.unsplash.com/photo-1506277886164-e25aa3f4ef7f?w=150&q=80'
}, {
  id: 3,
  name: 'Aminat Bello',
  location: 'Ikoyi, Lagos',
  property: 'Park Lane Phase II',
  quote: 'The level of luxury and attention to detail at Park Lane is unmatched. The customer service team was exceptional from inspection to handover.',
  avatar: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=150&q=80'
}, {
  id: 4,
  name: 'Dr. Kunle Afolabi',
  location: 'Ibadan',
  property: 'Royal Heights',
  quote: 'I bought two plots for land banking. The title documents were processed swiftly, and the estate layout is highly commendable.',
  avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&q=80'
}, {
  id: 5,
  name: 'Grace Okafor',
  location: 'Victoria Island, Lagos',
  property: 'Lekki Pride III',
  quote: 'A truly professional real estate company. They delivered exactly what was in the brochure, and the payment plan was very flexible.',
  avatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=150&q=80'
}, {
  id: 6,
  name: 'Femi Peters',
  location: 'London, UK',
  property: 'Zylus Court Phase II',
  quote: 'I was skeptical about buying property back home, but Zylus changed my perspective. The entire process was handled with utmost integrity.',
  avatar: 'https://images.unsplash.com/photo-1522529599102-193c0d76b5b6?w=150&q=80'
}, {
  id: 7,
  name: 'Ngozi Nwosu',
  location: 'Port Harcourt',
  property: 'Tiwa Reserve Plots',
  quote: 'The ROI on my land investment has been incredible. Zylus properties appreciate fast because of their strategic locations.',
  avatar: 'https://images.unsplash.com/photo-1589156280159-27698a70f29e?w=150&q=80'
}, {
  id: 8,
  name: 'Tunde Bakare',
  location: 'Lagos Mainland',
  property: 'Epe Garden Plots',
  quote: 'Affordable entry into real estate without compromising on security and title verification. Highly recommended for first-time buyers.',
  avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&q=80'
}];
function VideoThumb({
  videoId,
  alt
}: {
  videoId: string;
  alt: string;
}) {
  const qualities = ['maxresdefault', 'sddefault', 'hqdefault'];
  const [idx, setIdx] = useState(0);
  const src = `https://img.youtube.com/vi/${videoId}/${qualities[idx]}.jpg`;
  const handleLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    if (img.naturalWidth < 200 && idx < qualities.length - 1) {
      setIdx(idx + 1);
    }
  };
  const handleError = () => {
    if (idx < qualities.length - 1) setIdx(idx + 1);
  };
  return <img key={src} src={src} alt={alt} onLoad={handleLoad} onError={handleError} className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700" data-id="element-1382" />;
}
export function Testimonials() {
  const [activeVideo, setActiveVideo] = useState<VideoTestimonial | null>(null);
  const [filter, setFilter] = useState<'All' | 'Building' | 'Land'>('All');
  const filteredVideos = videoTestimonials.filter(v => filter === 'All' || v.category === filter);
  return <div className="pt-28 pb-24" data-id="element-1383">
      {/* Hero */}
      <div className="relative bg-bone py-20 px-6 text-center overflow-hidden" data-id="element-1384">
        {/* Floating decorative orbs */}
        <motion.div aria-hidden animate={{
        y: [0, -20, 0],
        x: [0, 10, 0]
      }} transition={{
        duration: 8,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="absolute top-10 left-1/4 w-64 h-64 rounded-full bg-gold/10 blur-3xl pointer-events-none" data-id="element-1385" />
        <motion.div aria-hidden animate={{
        y: [0, 20, 0],
        x: [0, -15, 0]
      }} transition={{
        duration: 10,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="absolute bottom-0 right-1/4 w-72 h-72 rounded-full bg-ink/5 blur-3xl pointer-events-none" data-id="element-1386" />

        <div className="relative" data-id="element-1387">
          <motion.div initial="hidden" animate="show" variants={{
          hidden: {},
          show: {
            transition: {
              staggerChildren: 0.08
            }
          }
        }} className="flex items-center justify-center gap-2 mb-6" data-id="element-1388">
            {[1, 2, 3, 4, 5].map(star => <motion.div key={star} variants={{
            hidden: {
              opacity: 0,
              scale: 0,
              rotate: -45
            },
            show: {
              opacity: 1,
              scale: 1,
              rotate: 0
            }
          }} transition={{
            type: 'spring',
            stiffness: 260,
            damping: 14
          }} data-id="element-1389">
                <Star className="w-8 h-8 text-gold fill-gold" data-id="element-1390" />
              </motion.div>)}
          </motion.div>
          <motion.h1 initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.5,
          duration: 0.7,
          ease: [0.22, 1, 0.36, 1]
        }} className="text-5xl md:text-6xl font-serif text-ink mb-6" data-id="element-1391">
            Client Stories
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.7,
          duration: 0.6
        }} className="text-ink/60 max-w-2xl mx-auto text-lg" data-id="element-1392">
            Real Nigerians, real homes, real land. Hear directly from clients
            who trusted Zylus with their next chapter.
          </motion.p>
        </div>
      </div>

      {/* Video Testimonials */}
      <section className="max-w-[1280px] mx-auto px-6 md:px-12 mt-20" data-id="element-1393">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10" data-id="element-1394">
          <div data-id="element-1395">
            <div className="text-[11px] font-bold tracking-[0.25em] uppercase text-gold-dark mb-3" data-id="element-1396">
              Video Testimonials
            </div>
            <h2 className="text-3xl md:text-4xl font-serif text-ink" data-id="element-1397">
              Hear it from them.
            </h2>
            <p className="text-ink/60 mt-3 max-w-xl text-sm" data-id="element-1398">
              Filmed at our estates and on inspection days — clients sharing
              their Zylus journey, in their own words.
            </p>
          </div>

          {/* Filter pills */}
          <div className="flex flex-wrap gap-2" data-id="element-1399">
            {(['All', 'Building', 'Land'] as const).map(cat => {
            const Icon = cat === 'Building' ? Building2 : cat === 'Land' ? TreePine : Star;
            return <button key={cat} onClick={() => setFilter(cat)} className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-xs font-bold uppercase tracking-widest transition-all ${filter === cat ? 'bg-ink text-white shadow-md' : 'bg-white text-ink/70 border border-ink/10 hover:border-ink/30'}`} data-id="element-1400">
                  <Icon className="w-3.5 h-3.5" data-id="element-1401" />
                  {cat}
                </button>;
          })}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1402">
          <AnimatePresence mode="popLayout" data-id="element-1403">
            {filteredVideos.map((v, i) => <motion.button key={v.id} layout initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            scale: 0.9
          }} transition={{
            delay: i % 3 * 0.08,
            duration: 0.5
          }} whileHover={{
            y: -6
          }} onClick={() => setActiveVideo(v)} className="group text-left bg-white rounded-2xl overflow-hidden shadow-sm border border-ink/5 hover:shadow-xl hover:border-gold/30 transition-all duration-500" data-id="element-1404">
                <div className="relative aspect-video overflow-hidden bg-ink" data-id="element-1405">
                  <VideoThumb videoId={v.videoId} alt={v.name} data-id="element-1406" />
                  {/* Play overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-ink/20 to-transparent" data-id="element-1407" />
                  <motion.div whileHover={{
                scale: 1.1
              }} className="absolute inset-0 flex items-center justify-center" data-id="element-1408">
                    <div className="w-16 h-16 rounded-full bg-gold flex items-center justify-center shadow-2xl" data-id="element-1409">
                      <Play className="w-6 h-6 text-ink fill-ink ml-1" data-id="element-1410" />
                    </div>
                  </motion.div>
                  {/* Category badge */}
                  <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm text-ink px-3 py-1.5 rounded-full text-[10px] font-bold tracking-widest uppercase flex items-center gap-1.5" data-id="element-1411">
                    {v.category === 'Building' ? <Building2 className="w-3 h-3" data-id="element-1412" /> : <TreePine className="w-3 h-3" data-id="element-1413" />}
                    {v.category}
                  </div>
                </div>
                <div className="p-6" data-id="element-1414">
                  <p className="text-ink/70 text-sm italic line-clamp-3" data-id="element-1415">
                    "{v.quote}"
                  </p>
                </div>
              </motion.button>)}
          </AnimatePresence>
        </div>
      </section>

      {/* Featured Large Quote */}
      <div className="max-w-[1280px] mx-auto px-6 md:px-12 mt-24" data-id="element-1416">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="bg-ink rounded-3xl p-10 md:p-16 text-center relative overflow-hidden" data-id="element-1417">
          <Quote className="absolute top-8 left-8 w-24 h-24 text-white/5 rotate-180" data-id="element-1418" />
          <Quote className="absolute bottom-8 right-8 w-24 h-24 text-white/5" data-id="element-1419" />

          <div className="flex justify-center gap-1 mb-8" data-id="element-1420">
            {[1, 2, 3, 4, 5].map(star => <Star key={star} className="w-5 h-5 text-gold fill-gold" data-id="element-1421" />)}
          </div>

          <p className="text-2xl md:text-4xl font-serif text-white leading-relaxed max-w-4xl mx-auto mb-10 relative z-10" data-id="element-1422">
            "Zylus Homes didn't just sell me a property; they delivered peace of
            mind. The transparency, the quality of construction, and the
            post-sales support have been absolutely phenomenal."
          </p>

          <motion.div initial={{
          opacity: 0,
          scale: 0.85
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.3,
          type: 'spring',
          stiffness: 120
        }} className="flex flex-col items-center relative z-10" data-id="element-1423">
            <motion.img whileHover={{
            scale: 1.08,
            rotate: 3
          }} transition={{
            type: 'spring',
            stiffness: 300
          }} src="https://images.unsplash.com/photo-1618077360395-f3068be8e001?w=200&q=80" alt="Chief Adebowale Ogunleye" className="w-20 h-20 rounded-full border-2 border-gold mb-4 object-cover shadow-2xl" data-id="element-1424" />
            <div className="text-white font-semibold" data-id="element-1425">
              Chief Adebowale Ogunleye
            </div>
            <div className="text-gold text-sm" data-id="element-1426">Multiple Property Investor</div>
          </motion.div>
        </motion.div>
      </div>

      {/* Written Testimonials Masonry */}
      <div className="max-w-[1280px] mx-auto px-6 md:px-12 mt-20" data-id="element-1427">
        <div className="mb-10" data-id="element-1428">
          <div className="text-[11px] font-bold tracking-[0.25em] uppercase text-gold-dark mb-3" data-id="element-1429">
            Written Reviews
          </div>
          <h2 className="text-3xl md:text-4xl font-serif text-ink" data-id="element-1430">
            Stories from across Nigeria & beyond.
          </h2>
        </div>

        <div className="columns-1 md:columns-2 lg:columns-3 gap-8 space-y-8" data-id="element-1431">
          {writtenTestimonials.map((t, i) => <motion.div key={t.id} initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i % 3 * 0.1,
          duration: 0.6
        }} className="break-inside-avoid bg-white rounded-3xl p-8 border border-ink/5 shadow-sm hover:shadow-xl transition-shadow" data-id="element-1432">
              <div className="flex items-center gap-1 mb-6" data-id="element-1433">
                {[1, 2, 3, 4, 5].map(star => <Star key={star} className="w-4 h-4 text-gold fill-gold" data-id="element-1434" />)}
              </div>

              <p className="text-ink/80 leading-relaxed mb-8 italic" data-id="element-1435">
                "{t.quote}"
              </p>

              <div className="flex items-center gap-4 pt-6 border-t border-ink/5" data-id="element-1436">
                <img src={t.avatar} alt={t.name} className="w-12 h-12 rounded-full object-cover" data-id="element-1437" />
                <div data-id="element-1438">
                  <div className="font-semibold text-ink text-sm" data-id="element-1439">{t.name}</div>
                  <div className="text-xs text-ink/50" data-id="element-1440">{t.location}</div>
                </div>
              </div>
            </motion.div>)}
        </div>

        {/* CTA */}
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} className="mt-24 text-center" data-id="element-1441">
          <motion.h3 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl font-serif text-ink mb-6" data-id="element-1442">
            Ready to write your own story?
          </motion.h3>
          <motion.div whileHover={{
          scale: 1.05
        }} whileTap={{
          scale: 0.97
        }} className="inline-block" data-id="element-1443">
            <Link to="/#book" className="inline-flex items-center gap-2 bg-gold hover:bg-gold-dark text-ink font-bold uppercase tracking-widest px-8 py-4 rounded-full transition-colors shadow-lg shadow-gold/20" data-id="element-1444">
              Book an Inspection
              <motion.span animate={{
              x: [0, 4, 0]
            }} transition={{
              duration: 1.4,
              repeat: Infinity
            }} data-id="element-1445">
                <ArrowRight className="w-4 h-4" data-id="element-1446" />
              </motion.span>
            </Link>
          </motion.div>
        </motion.div>
      </div>

      {/* Video Modal */}
      <AnimatePresence data-id="element-1447">
        {activeVideo && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={() => setActiveVideo(null)} className="fixed inset-0 z-[100] bg-ink/90 backdrop-blur-sm flex items-center justify-center p-4 md:p-8" data-id="element-1448">
            <motion.div initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.9,
          opacity: 0
        }} onClick={e => e.stopPropagation()} className="relative w-full max-w-4xl bg-ink rounded-2xl overflow-hidden shadow-2xl" data-id="element-1449">
              <button onClick={() => setActiveVideo(null)} aria-label="Close video" className="absolute -top-12 right-0 md:top-4 md:right-4 z-10 w-10 h-10 rounded-full bg-white/10 hover:bg-gold hover:text-ink text-white flex items-center justify-center transition-colors" data-id="element-1450">
                <X className="w-5 h-5" data-id="element-1451" />
              </button>
              <div className="aspect-video w-full" data-id="element-1452">
                <iframe src={`https://www.youtube.com/embed/${activeVideo.videoId}?autoplay=1&rel=0`} title={activeVideo.name} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="w-full h-full" data-id="element-1453" />
              </div>
              <div className="p-6 md:p-8 bg-ink text-white flex items-center justify-between gap-4 flex-wrap" data-id="element-1454">
                <p className="text-white/70 italic text-sm leading-relaxed flex-1 min-w-[200px]" data-id="element-1455">
                  "{activeVideo.quote}"
                </p>
                <div className="text-[10px] font-bold uppercase tracking-widest text-gold flex items-center gap-1.5 bg-white/5 px-3 py-1.5 rounded-full" data-id="element-1456">
                  {activeVideo.category === 'Building' ? <Building2 className="w-3 h-3" data-id="element-1457" /> : <TreePine className="w-3 h-3" data-id="element-1458" />}
                  {activeVideo.category}
                </div>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}